export type InviteEmailContent = {
  subject: string;
  plainText: string;
  html: string;
};

type TenantInviteParams = {
  recipientName: string;
  companyName: string;
  inviteUrl: string;
  expiresInDays?: number;
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function greetingName(name: string): string {
  const trimmed = name.trim();
  return trimmed || 'συνάδελφε';
}

/** Keep in sync with backend/src/mail/invite-email.template.ts */
export function buildTenantInviteEmail(
  params: TenantInviteParams,
): InviteEmailContent {
  const expiresInDays = params.expiresInDays ?? 7;
  const name = greetingName(params.recipientName);
  const company = params.companyName.trim();
  const url = params.inviteUrl.trim();

  const subject = `Πρόσκληση στο BalanceFlow — ${company}`;

  const plainText = [
    `Αγαπητέ/ή ${name},`,
    '',
    `Καλωσορίσατε στο BalanceFlow.`,
    '',
    `Σας προσκαλούμε να ενεργοποιήσετε τον λογαριασμό διαχειριστή για την επιχείρηση «${company}».`,
    '',
    'Με ένα κλικ ορίζετε τον κωδικό σας και μπαίνετε στην πλατφόρμα διαχείρισης πελατών, τιμολογίων και υπολοίπων.',
    '',
    'Σύνδεσμος ενεργοποίησης:',
    url,
    '',
    `Ισχύει για ${expiresInDays} ημέρες.`,
    'Αν δεν ζητήσατε αυτή την πρόσκληση, αγνοήστε το μήνυμα.',
    '',
    'Με εκτίμηση,',
    'Η ομάδα BalanceFlow',
  ].join('\n');

  const safeName = escapeHtml(name);
  const safeCompany = escapeHtml(company);
  const safeUrl = escapeHtml(url);

  const html = `<!DOCTYPE html>
<html lang="el">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <meta http-equiv="X-UA-Compatible" content="IE=edge" />
  <title>${escapeHtml(subject)}</title>
</head>
<body style="margin:0;padding:0;background-color:#e8eef4;font-family:'Segoe UI',Roboto,Helvetica,Arial,sans-serif;-webkit-font-smoothing:antialiased;">
  <div style="display:none;max-height:0;overflow:hidden;opacity:0;">
    Πρόσκληση διαχειριστή για «${safeCompany}» στο BalanceFlow — ορίστε κωδικό και συνδεθείτε.
  </div>
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#e8eef4;">
    <tr>
      <td align="center" style="padding:40px 16px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:580px;">
          <tr>
            <td align="center" style="padding:0 0 20px 0;">
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="width:36px;height:36px;border-radius:9px;background-color:#0f2744;text-align:center;vertical-align:middle;font-size:12px;font-weight:700;color:#5eead4;letter-spacing:0.04em;">BF</td>
                  <td style="padding-left:10px;font-size:16px;font-weight:650;color:#0f2744;letter-spacing:-0.02em;">BalanceFlow</td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td style="background-color:#ffffff;border-radius:16px;overflow:hidden;border:1px solid #d5dee8;box-shadow:0 8px 24px rgba(15,39,68,0.08);">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="background-color:#0f2744;padding:0;">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <td style="height:4px;background-color:#14b8a6;font-size:0;line-height:0;">&nbsp;</td>
                      </tr>
                    </table>
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                      <tr>
                        <td style="padding:28px 36px 24px 36px;">
                          <p style="margin:0 0 6px 0;font-size:11px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;color:#5eead4;">Πρόσκληση διαχειριστή</p>
                          <h1 style="margin:0;font-size:22px;line-height:1.35;font-weight:650;color:#ffffff;letter-spacing:-0.02em;">Καλωσορίσατε στο BalanceFlow</h1>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td style="padding:32px 36px 8px 36px;">
                    <p style="margin:0 0 18px 0;font-size:16px;line-height:1.5;color:#0f172a;">Αγαπητέ/ή <strong>${safeName}</strong>,</p>
                    <p style="margin:0 0 16px 0;font-size:15px;line-height:1.65;color:#334155;">
                      Σας προσκαλούμε να ενεργοποιήσετε τον λογαριασμό διαχειριστή για την επιχείρηση
                    </p>
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 20px 0;">
                      <tr>
                        <td style="background-color:#f0fdfa;border:1px solid #99f6e4;border-radius:10px;padding:14px 16px;">
                          <p style="margin:0;font-size:11px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;color:#0f766e;">Επιχείρηση</p>
                          <p style="margin:4px 0 0 0;font-size:17px;font-weight:650;color:#0f2744;letter-spacing:-0.01em;">${safeCompany}</p>
                        </td>
                      </tr>
                    </table>
                    <p style="margin:0 0 28px 0;font-size:15px;line-height:1.65;color:#334155;">
                      Ορίστε τον κωδικό σας και ξεκινήστε τη διαχείριση πελατών, τιμολογίων και υπολοίπων σε ένα μέρος.
                    </p>
                    <table role="presentation" cellpadding="0" cellspacing="0" style="margin:0 auto 28px auto;">
                      <tr>
                        <td align="center" style="border-radius:10px;background-color:#0d9488;">
                          <a href="${safeUrl}" style="display:inline-block;padding:14px 28px;font-size:15px;font-weight:650;color:#ffffff;text-decoration:none;letter-spacing:-0.01em;">
                            Ορισμός κωδικού &amp; είσοδος
                          </a>
                        </td>
                      </tr>
                    </table>
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 24px 0;">
                      <tr>
                        <td style="background-color:#f8fafc;border-radius:10px;padding:14px 16px;border:1px solid #e2e8f0;">
                          <p style="margin:0 0 6px 0;font-size:12px;font-weight:600;color:#64748b;">Εναλλακτικός σύνδεσμος</p>
                          <a href="${safeUrl}" style="font-size:12px;line-height:1.5;color:#0d9488;word-break:break-all;text-decoration:underline;">${safeUrl}</a>
                        </td>
                      </tr>
                    </table>
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 8px 0;">
                      <tr>
                        <td width="50%" style="padding-right:6px;vertical-align:top;">
                          <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                            <tr>
                              <td style="background-color:#f8fafc;border-radius:10px;padding:12px 14px;border:1px solid #e2e8f0;">
                                <p style="margin:0;font-size:11px;font-weight:600;color:#94a3b8;text-transform:uppercase;letter-spacing:0.06em;">Ισχύς</p>
                                <p style="margin:4px 0 0 0;font-size:14px;font-weight:650;color:#0f2744;">${expiresInDays} ημέρες</p>
                              </td>
                            </tr>
                          </table>
                        </td>
                        <td width="50%" style="padding-left:6px;vertical-align:top;">
                          <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                            <tr>
                              <td style="background-color:#f8fafc;border-radius:10px;padding:12px 14px;border:1px solid #e2e8f0;">
                                <p style="margin:0;font-size:11px;font-weight:600;color:#94a3b8;text-transform:uppercase;letter-spacing:0.06em;">Ρόλος</p>
                                <p style="margin:4px 0 0 0;font-size:14px;font-weight:650;color:#0f2744;">Διαχειριστής</p>
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td style="padding:16px 36px 32px 36px;">
                    <p style="margin:0 0 16px 0;font-size:13px;line-height:1.55;color:#94a3b8;">
                      Αν δεν ζητήσατε αυτή την πρόσκληση, αγνοήστε το μήνυμα.
                    </p>
                    <p style="margin:0;font-size:14px;line-height:1.5;color:#334155;">
                      Με εκτίμηση,<br />
                      <strong style="color:#0f2744;">Η ομάδα BalanceFlow</strong>
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td align="center" style="padding:20px 16px 0 16px;">
              <p style="margin:0;font-size:11px;line-height:1.5;color:#94a3b8;">
                Αυτό το μήνυμα στάλθηκε αυτόματα από το BalanceFlow.<br />
                Μην απαντάτε σε αυτό το email.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

  return { subject, plainText, html };
}
